import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  apiGetArtworkTemplateSlots,
  apiRemoveArtworkTemplate,
  apiUploadArtworkTemplate,
} from "../api";
import { refreshArtworkTemplates } from "../components/ArtworkTemplateLink";
import SettingsPanel from "../components/SettingsPanel";
import { THEME as C } from "../components/dashboardTheme";
import SettingHelp from "./Settings/settingsHelp";

/**
 * Admin screen for every artwork template on the platform.
 * ────────────────────────────────────────────────────────────────────────────
 * One row per image upload slot — a restaurant logo, a 1440x175 shop banner and
 * a 1000x1000 product shot are three different canvases, so each gets its own
 * template rather than sharing one.
 *
 * Two things this screen exists to prevent:
 *
 *   UPLOADING BLIND. Choosing a file never uploads it. It opens a confirmation
 *   showing which slot the file is bound for, which screens will start offering
 *   it, and a rendered preview of the actual PDF — so "which template is this
 *   and where does it go" is answered before anything is written, not after.
 *
 *   IMPLYING COVERAGE THAT DOES NOT EXIST. A slot whose `usedOn` is empty says
 *   so in amber. Uploading to it is allowed — it just will not appear anywhere
 *   until a screen renders that slot.
 *
 * The slot list comes from the server rather than being duplicated here, so
 * adding a slot to the registry makes it appear on this screen with no frontend
 * change. Uploads and removals are admin-gated server-side.
 */
const MAX_MB = 10;

const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });

/** Where a slot's template shows up, or an honest admission that it doesn't. */
const UsedOn = ({ usedOn }) => {
  if (!usedOn?.length) {
    return (
      <div
        style={{
          marginTop: 6,
          fontSize: 11.5,
          color: "#8a5a00",
          background: C.amberSoft,
          border: "1px solid #f5deb0",
          borderRadius: C.radiusSm,
          padding: "5px 9px",
          display: "inline-block",
        }}
      >
        <i className="fas fa-triangle-exclamation" style={{ marginRight: 6 }} />
        No screen offers this template yet, so one uploaded here is stored but no vendor sees it.
      </div>
    );
  }
  return (
    <div
      style={{
        marginTop: 6,
        display: "flex",
        flexWrap: "wrap",
        gap: 5,
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: 11.5, color: C.textLight }}>Appears on</span>
      {usedOn.map((where) => (
        <span
          key={where}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: C.blue,
            background: C.blueSoft,
            borderRadius: 999,
            padding: "3px 9px",
          }}
        >
          {where}
        </span>
      ))}
    </div>
  );
};

const Thumb = ({ src, alt, onClick, size = 64 }) => {
  if (!src) {
    return (
      <div
        title="No preview. The Cloudinary account may not allow PDF thumbnails, or the template predates previews."
        style={{
          width: size,
          height: size,
          borderRadius: C.radiusSm,
          border: `1px dashed ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          color: C.textLight,
          background: C.bg,
          flexShrink: 0,
        }}
      >
        PDF
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        background: C.panel,
        borderRadius: C.radiusSm,
        border: `1px solid ${C.border}`,
        cursor: onClick ? "zoom-in" : "default",
        flexShrink: 0,
      }}
    />
  );
};

const ghostButton = (disabled, tone = "neutral") => ({
  padding: "6px 13px",
  borderRadius: C.radiusSm,
  border: `1px solid ${tone === "danger" ? "#f3c2cd" : C.border}`,
  background: tone === "danger" ? C.redSoft : C.panel,
  color: tone === "danger" ? C.red : C.textMid,
  fontSize: 12,
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const SlotRow = ({ slot, busySlot, onPick, onRemove, onZoom, last }) => {
  const inputRef = useRef(null);
  const template = slot.template;
  const busy = busySlot === slot.key;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "14px 0",
        borderBottom: last ? "none" : `1px solid ${C.border}`,
      }}
    >
      <Thumb
        src={template?.previewImageUrl}
        alt={`${slot.label} template preview`}
        onClick={template?.previewImageUrl ? () => onZoom(slot, template) : undefined}
      />

      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>
          {slot.label}
        </div>
        <div style={{ fontSize: 11.5, color: C.textLight, marginTop: 2 }}>
          {slot.spec}
        </div>
        <UsedOn usedOn={slot.usedOn} />
        {template ? (
          <div style={{ marginTop: 7, fontSize: 12, color: C.textMid }}>
            <strong style={{ color: C.text }}>{template.fileName}</strong>
            <span style={{ color: C.textLight }}>
              {" · "}
              {formatBytes(template.bytes)}
              {template.pageCount
                ? ` · ${template.pageCount} page${template.pageCount > 1 ? "s" : ""}`
                : ""}
              {" · "}
              {formatWhen(template.uploadedAt)}
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 7, fontSize: 12, color: C.textLight }}>
            No template uploaded.
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {template ? (
          <a
            href={template.downloadUrl || template.url}
            target="_blank"
            rel="noopener noreferrer"
            style={ghostButton(false)}
          >
            <i className="fas fa-download" style={{ fontSize: 11 }} />
            Download
          </a>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={(event) => onPick(slot, event)}
          style={{ display: "none" }}
        />
        {template ? (
          <button
            type="button"
            disabled={!!busySlot}
            onClick={() => inputRef.current?.click()}
            style={ghostButton(!!busySlot)}
          >
            {busy ? (
              <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 11 }} />
            ) : (
              <i className="fas fa-arrows-rotate" style={{ fontSize: 11 }} />
            )}
            {busy ? "Working…" : "Replace"}
          </button>
        ) : (
          <SettingsPanel.Action
            disabled={!!busySlot}
            onClick={() => inputRef.current?.click()}
            style={{ padding: "6px 14px", fontSize: 12 }}
          >
            {busy ? (
              <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 11 }} />
            ) : (
              <i className="fas fa-upload" style={{ fontSize: 11 }} />
            )}
            {busy ? "Working…" : "Upload"}
          </SettingsPanel.Action>
        )}
        {template ? (
          <button
            type="button"
            disabled={!!busySlot}
            onClick={() => onRemove(slot.key)}
            style={ghostButton(!!busySlot, "danger")}
          >
            <i className="fas fa-trash" style={{ fontSize: 11 }} />
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ArtworkTemplateSettings = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState(null);
  // { slot, file, objectUrl } — a chosen file awaiting confirmation.
  const [pending, setPending] = useState(null);
  // { slot, template } — an uploaded template being viewed larger.
  const [zoom, setZoom] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiGetArtworkTemplateSlots();
      setSlots(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      toast.error("Could not load the artwork template slots.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // A blob URL outlives the component unless it is handed back.
  useEffect(
    () => () => {
      if (pending?.objectUrl) URL.revokeObjectURL(pending.objectUrl);
    },
    [pending]
  );

  const groups = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      const key = slot.group || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(slot);
    });
    return [...map.entries()];
  }, [slots]);

  const configuredCount = slots.filter((slot) => slot.template).length;

  /* Choosing a file does NOT upload it — it opens the confirmation. */
  const handlePick = (slot, event) => {
    const file = event.target.files?.[0];
    // Clear immediately: without this, picking the same file twice in a row
    // after a cancel or a failure fires no change event at all.
    event.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      toast.error("An artwork template must be a PDF.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(
        `That file is ${(file.size / (1024 * 1024)).toFixed(1)}MB. The limit is ${MAX_MB}MB.`
      );
      return;
    }

    setPending({ slot, file, objectUrl: URL.createObjectURL(file) });
  };

  const closePending = () => {
    if (pending?.objectUrl) URL.revokeObjectURL(pending.objectUrl);
    setPending(null);
  };

  const confirmUpload = async () => {
    if (!pending) return;
    const { slot, file } = pending;
    closePending();
    setBusySlot(slot.key);
    try {
      const dataUrl = await readAsDataUrl(file);
      const { data } = await apiUploadArtworkTemplate(slot.key, dataUrl, file.name);
      setSlots((prev) =>
        prev.map((row) =>
          row.key === slot.key ? { ...row, template: data?.data || null } : row
        )
      );
      // Let every mounted download control pick this up without a page reload.
      refreshArtworkTemplates();
      toast.success(`${slot.label} template updated.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Could not upload the template. This change is restricted to administrators."
      );
    } finally {
      setBusySlot(null);
    }
  };

  const handleRemove = async (slotKey) => {
    setBusySlot(slotKey);
    try {
      await apiRemoveArtworkTemplate(slotKey);
      setSlots((prev) =>
        prev.map((slot) => (slot.key === slotKey ? { ...slot, template: null } : slot))
      );
      refreshArtworkTemplates();
      toast.success("Template removed.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove the template.");
    } finally {
      setBusySlot(null);
    }
  };

  return (
    <SettingsPanel
      icon="fa-file-pdf"
      title="Artwork Templates"
      subtitle={
        loading
          ? "Loading…"
          : `${configuredCount} of ${slots.length} slots have a template · PDF, up to ${MAX_MB}MB`
      }
      maxWidth={980}
    >
      <SettingsPanel.Section title={<>What an artwork template is<SettingHelp topic="artworkTemplates" /></>} icon="fa-circle-info">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: C.textMid }}>
          One downloadable PDF template per image upload slot. Vendors open it in
          Photoshop or Illustrator before they make the image, so nobody starts
          from a blank canvas, and a logo, a banner and a product shot each get
          a canvas that fits. A template is a guide only: it never checks or
          resizes what a vendor uploads; the upload screen does that. A slot
          with no template shows no download link on its screens. Replacing one
          takes effect everywhere within about a minute, with no deploy.
        </p>
      </SettingsPanel.Section>

      {loading ? (
        <SettingsPanel.Section>
          <div style={{ padding: "28px 0", textAlign: "center", color: C.textLight }}>
            <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 20 }} />
          </div>
        </SettingsPanel.Section>
      ) : (
        groups.map(([group, groupSlots]) => (
          <SettingsPanel.Section key={group} title={group} icon="fa-layer-group">
            {groupSlots.map((slot, i) => (
              <SlotRow
                key={slot.key}
                slot={slot}
                busySlot={busySlot}
                onPick={handlePick}
                onRemove={handleRemove}
                onZoom={(s, t) => setZoom({ slot: s, template: t })}
                last={i === groupSlots.length - 1}
              />
            ))}
          </SettingsPanel.Section>
        ))
      )}

      {/* ── Confirm what is going where, before anything is written ────────── */}
      <Modal show={!!pending} onHide={closePending} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Upload to “{pending?.slot?.label}”
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: C.radiusSm,
              padding: 12,
              marginBottom: 14,
              fontSize: 12.5,
              color: C.textMid,
            }}
          >
            <div>
              <strong style={{ color: C.text }}>{pending?.file?.name}</strong>
              <span style={{ color: C.textLight }}>
                {" · "}
                {formatBytes(pending?.file?.size)}
              </span>
            </div>
            <div style={{ marginTop: 2 }}>{pending?.slot?.spec}</div>
            <UsedOn usedOn={pending?.slot?.usedOn} />
            {pending?.slot?.template ? (
              <div style={{ color: "#8a5a00", marginTop: 8 }}>
                This replaces the current template
                {pending.slot.template.fileName
                  ? ` (${pending.slot.template.fileName})`
                  : ""}
                . The old file is overwritten and cannot be recovered.
              </div>
            ) : null}
          </div>

          {/* Rendered from a blob URL rather than a data: URL — browsers are
              far more consistent about displaying the former inline. */}
          <embed
            src={pending?.objectUrl}
            type="application/pdf"
            style={{
              width: "100%",
              height: 460,
              border: `1px solid ${C.border}`,
              borderRadius: C.radiusSm,
              background: C.panel,
            }}
          />
          <div style={{ fontSize: 11.5, color: C.textLight, marginTop: 6 }}>
            If the preview does not render, your browser is refusing to display
            the PDF inline. The file itself is still fine.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" onClick={closePending} style={ghostButton(false)}>
            Cancel
          </button>
          <SettingsPanel.Action onClick={confirmUpload}>
            <i className="fas fa-upload" style={{ fontSize: 12 }} />
            Upload to {pending?.slot?.label}
          </SettingsPanel.Action>
        </Modal.Footer>
      </Modal>

      {/* ── Look at what a slot currently holds ────────────────────────────── */}
      <Modal show={!!zoom} onHide={() => setZoom(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {zoom?.slot?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {zoom?.template?.previewImageUrl ? (
            <img
              src={zoom.template.previewImageUrl}
              alt={`${zoom.slot.label} template`}
              style={{ maxWidth: "100%", borderRadius: C.radiusSm }}
            />
          ) : null}
          {zoom?.template?.pageCount > 1 ? (
            <div style={{ fontSize: 12, color: C.textLight, marginTop: 8 }}>
              Page 1 of {zoom.template.pageCount}. Download the file to see the
              rest.
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <a
            href={zoom?.template?.downloadUrl || zoom?.template?.url}
            target="_blank"
            rel="noopener noreferrer"
            style={ghostButton(false)}
          >
            <i className="fas fa-download" style={{ fontSize: 11 }} />
            Download
          </a>
        </Modal.Footer>
      </Modal>
    </SettingsPanel>
  );
};

export default ArtworkTemplateSettings;
